import { useCallback, useState } from 'react';
import XLSX from 'xlsx';
import filesaver from 'filesaver.js';
import { i18n } from 'shared/i18n';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { fetchDocumentCssClasses } from 'entities/document';
import { useDesignDraft } from 'app/api/documents/useDesignDraft';
import { calculatePageOffset } from 'slate/components/page/utils';
import { styleToClassMap } from '../utils/class-map';
import { buildCssReportWorkbook, cssReportFileName, parseClassAttribute, ReportSection, ReportTable } from '../utils/css-report';

async function loadReportInput(documentSuperId: number | string): Promise<{ sections: ReportSection[]; tables: ReportTable[] }> {
  const data = await fetchDocumentCssClasses(documentSuperId);

  return {
    sections: data.sections.map((section) => ({
      name: section.name,
      pages: section.pages.map((cssClass) => ({ classes: parseClassAttribute(cssClass) })),
    })),
    tables: data.tables.map((table) => ({
      name: table.name,
      classes: parseClassAttribute(table.cssClass),
    })),
  };
}

export function useCssReportExport() {
  const {
    pDocument: { data: doc },
    designEntry,
    style,
    draftEntry,
  } = useDesignDraft({ styleType: 'form-classes', parsed: true });
  const [busy, setBusy] = useState(false);

  const exportReport = useCallback(async () => {
    if (!doc || busy) return;

    setBusy(true);
    try {
      const styleData = style.data as { content?: Record<string, unknown>; metadata?: Record<string, unknown> } | undefined;
      const documentMetadata = styleData?.metadata ?? designEntry?.metadata;
      const effectiveStyle = (draftEntry ? draftEntry.style ?? {} : styleData?.content ?? {}) as Record<string, unknown>;
      const effectiveMetadata = draftEntry ? draftEntry.metadata ?? documentMetadata : documentMetadata;
      const classMap = styleToClassMap(effectiveStyle, effectiveMetadata as Parameters<typeof styleToClassMap>[1]);

      const { sections, tables } = await loadReportInput(doc.superId);

      const pageNumbering = doc.content?.pageNumbering;
      const pageOffset = calculatePageOffset(pageNumbering);
      const showFrom = pageNumbering?.showFrom ?? 0;
      const workbook = buildCssReportWorkbook({ sections, tables, classMap, pageOffset, showFrom });
      const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      filesaver.saveAs(new Blob([output], { type: 'application/octet-stream' }), cssReportFileName(doc.name));
    } catch (e) {
      console.error(e);
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        title: i18n('dc:own-classes.export-failed'),
      });
    } finally {
      setBusy(false);
    }
  }, [doc, style.data, designEntry, draftEntry, busy]);

  return { exportReport, busy, ready: !!doc };
}
